import { useEffect, useState } from "react";
import { AlertTriangle, X, ExternalLink, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * Twilio A2P Reminder — persistent amber banner shown on every admin page
 * while the A2P brand identity is unverified (i.e. TCR is waiting on the
 * user to reply YES to the verification SMS).
 *
 * - Polls /api/settings/twilio/a2p-status once every 90s.
 * - Only renders when `summary === "identity_unverified"`.
 * - NOT dismissible — this is a business blocker, not a nag.
 * - Auto-hides the moment identity flips to VERIFIED (or state changes).
 */
export default function TwilioA2PReminder() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/twilio/a2p-status");
      setState(data);
    } catch {
      setState({ summary: "unknown" });
    }
  };

  const forceRefresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/settings/twilio/a2p-status?refresh=true");
      setState(data);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 90_000);   // poll every 90s
    return () => clearInterval(iv);
  }, []);

  if (!state || state.summary !== "identity_unverified") return null;

  const brand = (state.brands || [])[0] || {};
  const brandUrl = brand.sid
    ? `https://console.twilio.com/us1/develop/sms/regulatory-compliance/brands/${brand.sid}`
    : "https://console.twilio.com/us1/develop/sms/regulatory-compliance/brands";

  return (
    <div
      data-testid="twilio-a2p-reminder"
      style={{
        background: "#78350F",   // amber-900
        color: "#FFFBEB",
        padding: "10px 16px",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        borderBottom: "1px solid #92400E",
      }}
    >
      <AlertTriangle style={{ width: 16, height: 16, color: "#FCD34D", flexShrink: 0 }} />
      <span
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          background: "rgba(255,255,255,0.14)",
          padding: "3px 8px",
          borderRadius: 3,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        SMS BLOCKED
      </span>
      <span style={{ fontWeight: 600, flexShrink: 0 }}>
        Twilio A2P identity unverified —
      </span>
      <span style={{ opacity: 0.92, minWidth: 0 }}>
        Reply <strong style={{ background: "rgba(252,211,77,0.18)", padding: "1px 6px", borderRadius: 2, letterSpacing: 0.5 }}>YES</strong>
        {" "}to the TCR text on your registered phone to unlock SMS sending.
        {" "}All SMS steps (missed-call text-back, reactivation, review campaigns) stay paused until this clears.
      </span>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          onClick={forceRefresh}
          disabled={busy}
          data-testid="a2p-reminder-refresh"
          title="Re-poll Twilio for latest status"
          style={{
            background: "rgba(255,255,255,0.14)",
            color: "#FFFBEB",
            border: "1px solid rgba(252,211,77,0.35)",
            padding: "5px 10px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 3,
            cursor: busy ? "wait" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <RefreshCw style={{ width: 12, height: 12, animation: busy ? "spin 0.8s linear infinite" : "none" }} />
          {busy ? "Checking…" : "I replied — recheck"}
        </button>
        <a
          href={brandUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="a2p-reminder-brand-link"
          style={{
            background: "#FCD34D",
            color: "#78350F",
            padding: "5px 10px",
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 3,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          Open Twilio brand <ExternalLink style={{ width: 12, height: 12 }} />
        </a>
      </div>
      <style dangerouslySetInnerHTML={{ __html: "@keyframes spin { to { transform: rotate(360deg); } }" }} />
    </div>
  );
}
